import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import {
    FiCalendar,
    FiDollarSign,
    FiEye,
    FiHeart,
    FiPhone,
    FiTrendingUp,
    FiTruck,
    FiUsers
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import RecentActivity from '../../components/Dashboard/RecentActivity';
import StatsCard from '../../components/Dashboard/StatsCard';
import AnalyticsService from '../../services/analyticsService';
import CarService from '../../services/carService';

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    stats: {},
    salesData: [],
    carCategoryData: [],
    topPerformingCars: [],
    quickStats: {}
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch all dashboard data concurrently
        const [stats, salesTrend, categoryData, topCars, quickStatsData] = await Promise.all([
          AnalyticsService.getDashboardStats(),
          AnalyticsService.getSalesTrend(),
          AnalyticsService.getCarCategories(),
          CarService.getTopPerformingCars(),
          AnalyticsService.getQuickStats()
        ]);

        setDashboardData({
          stats: stats.data,
          salesData: salesTrend.data.map(item => ({
            month: item.month,
            sales: item.salesCount,
            revenue: item.revenue
          })),
          carCategoryData: categoryData.data.map((item, index) => ({
            name: item.category,
            value: item.count,
            color: ['#FF8C00', '#FF6B35', '#FFD700', '#FFA500', '#FF7F50'][index % 5]
          })),
          topPerformingCars: topCars.data.map(car => ({
            brand: car.make,
            model: car.model,
            views: car.analytics?.views || 0,
            inquiries: car.analytics?.inquiries || 0,
            id: car._id
          })),
          quickStats: quickStatsData.data
        });

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Fallback to demo data if API fails
        setDashboardData({
          stats: {
            totalCars: 1247,
            activeSellers: 89,
            totalRevenue: 24000000,
            carsSold: 156,
            totalCarsChange: 12,
            activeSellersChange: 5,
            totalRevenueChange: 18,
            carsSoldChange: 8
          },
          salesData: [
            { month: 'Jan', sales: 65, revenue: 2400000 },
            { month: 'Feb', sales: 59, revenue: 1800000 },
            { month: 'Mar', sales: 80, revenue: 3200000 },
            { month: 'Apr', sales: 81, revenue: 3400000 },
            { month: 'May', sales: 56, revenue: 2200000 },
            { month: 'Jun', sales: 95, revenue: 4100000 },
          ],
          carCategoryData: [
            { name: 'Sedan', value: 400, color: '#FF8C00' },
            { name: 'SUV', value: 300, color: '#FF6B35' },
            { name: 'Hatchback', value: 200, color: '#FFD700' },
            { name: 'Convertible', value: 100, color: '#FFA500' },
          ],
          topPerformingCars: [
            { brand: 'Maruti Suzuki', model: 'Swift', views: 1250, inquiries: 89 },
            { brand: 'Hyundai', model: 'i20', views: 1100, inquiries: 76 },
            { brand: 'Tata', model: 'Nexon', views: 980, inquiries: 65 },
            { brand: 'Honda', model: 'City', views: 850, inquiries: 58 },
          ],
          quickStats: {
            pageViewsToday: 12456,
            favoritesAdded: 234,
            inquiriesToday: 67,
            testDrivesBooked: 23
          }
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const statsCards = [
    {
      title: 'Total Cars Listed',
      value: dashboardData.stats.totalCars?.toLocaleString() || '0',
      change: `+${dashboardData.stats.totalCarsChange || 0}%`,
      trend: 'up',
      icon: FiTruck,
      color: 'blue'
    },
    {
      title: 'Active Sellers',
      value: dashboardData.stats.activeSellers?.toString() || '0',
      change: `+${dashboardData.stats.activeSellersChange || 0}%`,
      trend: 'up',
      icon: FiUsers,
      color: 'green'
    },
    {
      title: 'Total Revenue',
      value: `₹${((dashboardData.stats.totalRevenue || 0) / 10000000).toFixed(1)}Cr`,
      change: `+${dashboardData.stats.totalRevenueChange || 0}%`,
      trend: 'up',
      icon: FiDollarSign,
      color: 'orange'
    },
    {
      title: 'Cars Sold',
      value: dashboardData.stats.carsSold?.toString() || '0',
      change: `+${dashboardData.stats.carsSoldChange || 0}%`,
      trend: 'up',
      icon: FiTrendingUp,
      color: 'purple'
    }
  ];

  const quickStats = [
    { 
      label: 'Page Views Today', 
      value: dashboardData.quickStats.pageViewsToday?.toLocaleString() || '0', 
      icon: FiEye 
    },
    { 
      label: 'Favorites Added', 
      value: dashboardData.quickStats.favoritesAdded?.toString() || '0', 
      icon: FiHeart 
    },
    { 
      label: 'Inquiries Today', 
      value: dashboardData.quickStats.inquiriesToday?.toString() || '0', 
      icon: FiPhone 
    },
    { 
      label: 'Test Drives Booked', 
      value: dashboardData.quickStats.testDrivesBooked?.toString() || '0', 
      icon: FiCalendar 
    },
  ];

  const handleAddNewCar = () => {
    navigate('/car-management/add');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
          <p className="mt-2 text-gray-600">Welcome back! Here's what's happening with your car listings.</p>
        </div>
        <div className="mt-4 sm:mt-0">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAddNewCar}
            className="inline-flex items-center px-6 py-3 gradient-bg text-white font-medium rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
          >
            <FiTruck className="mr-2 h-5 w-5" />
            Add New Car
          </motion.button>
        </div>
      </motion.div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <StatsCard {...stat} />
          </motion.div>
        ))}
      </div>

      {/* Quick Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white rounded-2xl p-6 card-shadow"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Today's Quick Stats</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {quickStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              whileHover={{ scale: 1.02 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 mb-3">
                <stat.icon className="h-6 w-6 text-orange-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Sales Trend */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Sales Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dashboardData.salesData}>
              <defs>
                <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FF8C00" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#FF8C00" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  border: '1px solid rgba(255, 140, 0, 0.2)',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="sales" 
                stroke="#FF8C00" 
                fillOpacity={1} 
                fill="url(#colorSales)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Car Categories */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Car Categories</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={dashboardData.carCategoryData}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {dashboardData.carCategoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* Revenue Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-white rounded-2xl p-6 card-shadow"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Monthly Revenue</h3>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={dashboardData.salesData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip 
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid rgba(255, 140, 0, 0.2)',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
              formatter={(value) => [`₹${(value/100000).toFixed(1)}L`, 'Revenue']}
            />
            <Bar dataKey="revenue" fill="#FF8C00" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </motion.div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Top Performing Cars */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="lg:col-span-2 bg-white rounded-2xl p-6 card-shadow"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Performing Cars</h3>
          <div className="space-y-4">
            {dashboardData.topPerformingCars.map((car, index) => (
              <motion.div
                key={car.id || `${car.brand}-${car.model}`}
                whileHover={{ scale: 1.02 }}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-orange-50 transition-colors duration-200"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 gradient-bg rounded-lg flex items-center justify-center text-white font-semibold">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{car.brand} {car.model}</p>
                    <p className="text-sm text-gray-600">{car.views} views</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-orange-600">{car.inquiries} inquiries</p>
                  <p className="text-sm text-gray-500">This month</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
        >
          <RecentActivity />
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;